import { Component, computed, inject, model, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { IconModule } from '@insolution/core-data-access';
import { ListPatientsByTenantIdQuery } from '@insolution/graphql-data-access';
import { PatientService } from '@insolution/patient-data-access';
import { PatientsTable } from '@insolution/patient-ui';
import { NoSearchResults, SearchInput } from '@insolution/search-ui';
import { TranslatePipe } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs';

@Component({
  selector: 'lib-patients',
  imports: [TranslatePipe, IconModule, RouterLink, PatientsTable, SearchInput, NoSearchResults],
  templateUrl: './patients.html',
  styleUrl: './patients.scss',
})
export class Patients {
  private patientService = inject(PatientService);
  private router = inject(Router);

  protected filteredPatients = signal<ListPatientsByTenantIdQuery['listPatientsByTenantId']>([]);
  protected searchTerms = model('');
  protected isSearching = signal(false);
  protected hasNoSearchResults = computed(() => this.isSearching() && this.filteredPatients().length === 0);
  protected totalNumberOfPatients = signal(0);

  constructor() {
    this.setTotalNumberOfPatients();

    toObservable(this.searchTerms)
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchTerm) => {
          if (searchTerm.trim().length <= 2) {
            this.isSearching.set(false);
          }
        }),
        filter((term) => term.trim().length > 2),
        takeUntilDestroyed()
      )
      .subscribe((searchTerms) => {
        this.isSearching.set(true);
        this.setFilteredPatients(searchTerms);
      });
  }

  protected navigateToPatientDetails(patientId: string) {
    this.router.navigate(['/patients', patientId]);
  }

  private async setFilteredPatients(searchTerms: string) {
    const results = await this.patientService.getFilteredPatients(searchTerms);
    this.filteredPatients.set(results);
  }

  private async setTotalNumberOfPatients() {
    const count = await this.patientService.getTotalNumberOfPatients();
    this.totalNumberOfPatients.set(count);
  }
}
